import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneOutgoing } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function CallTracking() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [agent, setAgent] = useState("all");
  const navigate = useNavigate();

  const load = () => api.get("/call-tasks").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const agents = [...new Set(rows.map((r) => r.assigned_to).filter(Boolean))].sort();
  let view = rows;
  if (status !== "all") view = view.filter((r) => (status === "pending" ? r.status !== "Done" : r.status === "Done"));
  if (agent !== "all") view = view.filter((r) => r.assigned_to === agent);
  const pendingCount = rows.filter((r) => r.status !== "Done").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-2"><PhoneOutgoing size={24} className="text-primary" /> Outbound Calls</h1>
          <p className="text-muted-foreground mt-1">Track every outbound call assigned to the team — {pendingCount} pending of {rows.length} total.</p>
        </div>
        <div className="w-56">
          <Select value={agent} onValueChange={setAgent}>
            <SelectTrigger className="rounded-sm" data-testid="call-agent-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All agents</SelectItem>
              {agents.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="all" className="rounded-sm" data-testid="calls-all">All</TabsTrigger>
          <TabsTrigger value="pending" className="rounded-sm" data-testid="calls-pending">Pending</TabsTrigger>
          <TabsTrigger value="done" className="rounded-sm" data-testid="calls-done">Done</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="rounded-sm border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Customer</TableHead><TableHead>Phone</TableHead><TableHead>Reason</TableHead>
              <TableHead>Assigned to</TableHead><TableHead>Status</TableHead><TableHead>Outcome note</TableHead><TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="call-tracking-body">
            {view.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No calls in this view.</TableCell></TableRow>}
            {view.map((t) => (
              <TableRow key={t.id} data-testid={`track-row-${t.id}`}>
                <TableCell className="font-medium cursor-pointer hover:text-primary" onClick={() => navigate(`/clients/${t.customer_id}`)}>{t.customer_name}</TableCell>
                <TableCell className="font-mono text-sm">{t.phone || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm">{t.reason}</Badge></TableCell>
                <TableCell className="text-sm">{t.assigned_to || "—"}</TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm ${t.status === "Done" ? "bg-success/15 text-success border-success/30" : "bg-warning/15 text-warning border-warning/30"}`}>{t.status}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[240px] truncate" title={t.note}>{t.note || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{t.updated_at ? new Date(t.updated_at).toLocaleDateString() : (t.created_at ? new Date(t.created_at).toLocaleDateString() : "—")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
